/**
 * useGeofenceLayer Hook
 *
 * Creates and manages DeckGL layers for rendering geofences with GPU acceleration.
 * This hook handles both circle geofences (ScatterplotLayer) and polygon geofences
 * (GeoJsonLayer) with proper memoization to prevent unnecessary layer recreation.
 *
 * Key features:
 * - GPU-accelerated rendering via DeckGL ScatterplotLayer and GeoJsonLayer
 * - Hover effects with increased opacity and stroke weight
 * - Picking support for hover/click interactions
 * - Proper sorting so smaller geofences render on top
 */

import { Layer, PickingInfo } from '@deck.gl/core';
import { GeoJsonLayer, ScatterplotLayer } from '@deck.gl/layers';
import { polygon as turfPolygon } from '@turf/helpers';
import { useCallback, useMemo } from 'react';

import {
  CircleGeofenceDatum,
  GEOFENCE_ADDITIONAL_STROKE_WEIGHT,
  GEOFENCE_HOVER_FILL_OPACITY,
  GEOFENCE_HOVER_STROKE_WEIGHT,
  GEOFENCE_STROKE_WEIGHT,
  PolygonGeofenceDatum,
  RGBAColor,
} from '~components/Maps/v2/utils/transformSiteData';

/**
 * Information returned when a geofence is picked (hovered or clicked).
 */
export interface GeofencePickInfo {
  /** The geofence data that was picked (circle or polygon) */
  geofence: CircleGeofenceDatum | PolygonGeofenceDatum;
  /** Whether this is a circle geofence */
  isCircle: boolean;
  /** Screen X coordinate of the pick event */
  x: number;
  /** Screen Y coordinate of the pick event */
  y: number;
}

/**
 * Options for configuring the geofence layer behavior.
 */
interface UseGeofenceLayerOptions {
  /** Callback when a geofence is hovered */
  onHover?: (info: GeofencePickInfo | null) => void;
  /** Callback when a geofence is clicked */
  onClick?: (info: GeofencePickInfo) => void;
  /** ID of the currently hovered geofence (for styling) */
  hoveredGeofenceId?: string | null;
}

/**
 * Return type for the useGeofenceLayer hook.
 */
interface UseGeofenceLayerResult {
  /** The circle geofences layer for DeckGL */
  circleLayer: ScatterplotLayer<CircleGeofenceDatum> | null;
  /** The polygon geofences layer for DeckGL */
  polygonLayer: GeoJsonLayer | null;
  /** Combined array of all geofence layers for convenience */
  layers: Layer[];
}

/**
 * GeoJSON Feature type for polygon geofences.
 */
interface PolygonFeature {
  type: 'Feature';
  properties: PolygonGeofenceDatum;
  geometry: {
    type: 'Polygon';
    coordinates: number[][][];
  };
}

/**
 * GeoJSON FeatureCollection type for polygon geofences.
 */
interface PolygonFeatureCollection {
  type: 'FeatureCollection';
  features: PolygonFeature[];
}

/**
 * Applies hover effect to a fill color by increasing opacity.
 *
 * @param color - Original RGBA color
 * @param isHovered - Whether the geofence is hovered
 * @returns Modified RGBA color with hover opacity if hovered
 */
const applyHoverFillColor = (color: RGBAColor, isHovered: boolean): RGBAColor => {
  if (!isHovered) {
    return color;
  }
  // Increase opacity from 25% (64) to 40% (102)
  return [color[0], color[1], color[2], GEOFENCE_HOVER_FILL_OPACITY];
};

/** Alpha multiplier applied to default (candidate) site geofences so they recede. */
const DEFAULT_SITE_GEOFENCE_ALPHA = 0.4;

/**
 * Fade a geofence color (fill or stroke) for default (candidate) sites so the
 * geofence doesn't compete with the muted default-site marker on top of it.
 */
const muteForDefaultSite = (color: RGBAColor, isDefaultSite?: boolean): RGBAColor =>
  isDefaultSite
    ? [
        color[0],
        color[1],
        color[2],
        Math.round((color[3] ?? 255) * DEFAULT_SITE_GEOFENCE_ALPHA),
      ]
    : color;

/**
 * Creates DeckGL layers for rendering geofences.
 *
 * This hook creates two layers:
 * 1. ScatterplotLayer - Renders circle geofences with accurate radius in meters
 * 2. GeoJsonLayer - Renders polygon geofences
 *
 * Both layers support picking for hover/click interactions and apply hover effects.
 *
 * @param circleData - Array of circle geofence data
 * @param polygonData - Array of polygon geofence data
 * @param options - Configuration options for layer behavior
 * @returns Object containing the created layers
 *
 * @example
 * ```tsx
 * const { layers } = useGeofenceLayer(circleGeofences, polygonGeofences, {
 *   onHover: (info) => setHoveredGeofence(info?.geofence ?? null),
 *   onClick: (info) => setSelectedGeofence(info.geofence),
 *   hoveredGeofenceId,
 * });
 *
 * // Pass layers to MapV2's overlayLayers prop
 * <MapV2 overlayLayers={[...layers, ...siteLayers, ...truckLayers]} />
 * ```
 */
export const useGeofenceLayer = (
  circleData: CircleGeofenceDatum[],
  polygonData: PolygonGeofenceDatum[],
  options: UseGeofenceLayerOptions = {},
): UseGeofenceLayerResult => {
  const { onHover, onClick, hoveredGeofenceId } = options;

  /**
   * Handler for DeckGL circle hover events.
   * Transforms the PickingInfo into our GeofencePickInfo format.
   */
  const handleCircleHover = useCallback(
    (info: PickingInfo<CircleGeofenceDatum>) => {
      if (!onHover) return;

      if (info.object) {
        onHover({
          geofence: info.object,
          isCircle: true,
          x: info.x,
          y: info.y,
        });
      } else {
        onHover(null);
      }
    },
    [onHover],
  );

  /**
   * Handler for DeckGL circle click events.
   */
  const handleCircleClick = useCallback(
    (info: PickingInfo<CircleGeofenceDatum>) => {
      if (!onClick) return;

      if (info.object) {
        onClick({
          geofence: info.object,
          isCircle: true,
          x: info.x,
          y: info.y,
        });
      }
    },
    [onClick],
  );

  /**
   * Handler for DeckGL polygon hover events.
   */
  const handlePolygonHover = useCallback(
    (info: PickingInfo<PolygonFeature>) => {
      if (!onHover) return;

      if (info.object) {
        onHover({
          geofence: info.object.properties,
          isCircle: false,
          x: info.x,
          y: info.y,
        });
      } else {
        onHover(null);
      }
    },
    [onHover],
  );

  /**
   * Handler for DeckGL polygon click events.
   */
  const handlePolygonClick = useCallback(
    (info: PickingInfo<PolygonFeature>) => {
      if (!onClick) return;

      if (info.object) {
        onClick({
          geofence: info.object.properties,
          isCircle: false,
          x: info.x,
          y: info.y,
        });
      }
    },
    [onClick],
  );

  /**
   * Creates the circle geofence layer (ScatterplotLayer).
   *
   * Key features:
   * - Uses meters as radius units for accurate map scaling
   * - Supports hover effect with increased opacity
   * - Sorted by radius descending so smaller circles are on top
   */
  const circleLayer = useMemo(() => {
    if (circleData.length === 0) {
      return null;
    }

    return new ScatterplotLayer<CircleGeofenceDatum>({
      id: 'geofence-circles-layer',
      data: circleData,

      // Position and radius
      getPosition: (d: CircleGeofenceDatum) => d.center,
      getRadius: (d: CircleGeofenceDatum) => d.radiusMeters,
      radiusUnits: 'meters', // Critical for accurate map scaling

      // Fill color with hover effect
      getFillColor: (d: CircleGeofenceDatum) =>
        muteForDefaultSite(
          applyHoverFillColor(d.fillColor, d.id === hoveredGeofenceId),
          d.isDefaultSite,
        ),
      filled: true,

      // Stroke - additional geofences get thicker strokes for visibility
      getLineColor: (d: CircleGeofenceDatum) =>
        muteForDefaultSite(d.strokeColor, d.isDefaultSite),
      getLineWidth: (d: CircleGeofenceDatum) => {
        if (d.id === hoveredGeofenceId) {
          return GEOFENCE_HOVER_STROKE_WEIGHT;
        }
        // Additional geofences get thicker strokes for better visibility when stacked
        return d.isPrimary ? GEOFENCE_STROKE_WEIGHT : GEOFENCE_ADDITIONAL_STROKE_WEIGHT;
      },
      lineWidthUnits: 'pixels',
      stroked: true,

      // Interaction settings
      pickable: true,
      onHover: handleCircleHover,
      onClick: handleCircleClick,

      // Performance settings
      updateTriggers: {
        getFillColor: hoveredGeofenceId, // Re-evaluate when hover changes
        getLineWidth: hoveredGeofenceId,
      },
    });
  }, [circleData, hoveredGeofenceId, handleCircleHover, handleCircleClick]);

  /**
   * Convert polygon data to GeoJSON FeatureCollection format.
   */
  const polygonFeatureCollection = useMemo((): PolygonFeatureCollection | null => {
    if (polygonData.length === 0) {
      return null;
    }

    const features: PolygonFeature[] = polygonData.map((polygon) => ({
      type: 'Feature' as const,
      properties: polygon,
      geometry: turfPolygon(polygon.coordinates).geometry,
    }));

    return {
      type: 'FeatureCollection',
      features,
    };
  }, [polygonData]);

  /**
   * Creates the polygon geofence layer (GeoJsonLayer).
   *
   * Key features:
   * - Renders GeoJSON polygons with fill and stroke
   * - Supports hover effect with increased opacity
   * - Sorted by area descending so smaller polygons are on top
   */
  const polygonLayer = useMemo(() => {
    if (!polygonFeatureCollection) {
      return null;
    }

    return new GeoJsonLayer<PolygonFeatureCollection>({
      id: 'geofence-polygons-layer',
      data: polygonFeatureCollection,

      // Fill color with hover effect
      // Type cast needed because GeoJsonLayer's internal typing doesn't match our custom PolygonFeature
      getFillColor: (feature) => {
        const props = (feature as unknown as PolygonFeature).properties;
        return muteForDefaultSite(
          applyHoverFillColor(props.fillColor, props.id === hoveredGeofenceId),
          props.isDefaultSite,
        );
      },
      filled: true,

      // Stroke - additional geofences get thicker strokes for visibility
      getLineColor: (feature) => {
        const props = (feature as unknown as PolygonFeature).properties;
        return muteForDefaultSite(props.strokeColor, props.isDefaultSite);
      },
      getLineWidth: (feature) => {
        const props = (feature as unknown as PolygonFeature).properties;
        if (props.id === hoveredGeofenceId) {
          return GEOFENCE_HOVER_STROKE_WEIGHT;
        }
        // Additional geofences get thicker strokes for better visibility when stacked
        return props.isPrimary
          ? GEOFENCE_STROKE_WEIGHT
          : GEOFENCE_ADDITIONAL_STROKE_WEIGHT;
      },
      lineWidthUnits: 'pixels',
      stroked: true,

      // Interaction settings
      pickable: true,
      onHover: handlePolygonHover,
      onClick: handlePolygonClick,

      // Performance settings
      updateTriggers: {
        getFillColor: hoveredGeofenceId, // Re-evaluate when hover changes
        getLineWidth: hoveredGeofenceId,
      },
    });
  }, [
    polygonFeatureCollection,
    hoveredGeofenceId,
    handlePolygonHover,
    handlePolygonClick,
  ]);

  /**
   * Combined layers array for easy consumption.
   * Order: polygons first (bottom), then circles (top for smaller geofences).
   */
  const layers = useMemo(() => {
    const result: Layer[] = [];

    // Polygon layer first (renders at bottom)
    if (polygonLayer) {
      result.push(polygonLayer);
    }

    // Circle layer second (renders on top)
    if (circleLayer) {
      result.push(circleLayer);
    }

    return result;
  }, [polygonLayer, circleLayer]);

  return {
    circleLayer,
    polygonLayer,
    layers,
  };
};
